import Spacer from 'features/shared/Spacer';
import React, { useEffect, useMemo, useState } from 'react';
import Chart from './Chart';
import SwitchTabs, { BlueLinksTabSmall } from 'features/shared/SwitchTabs';
import { SchoolsApi } from 'api';
import { Api } from 'api/cabmun-api-public';
import LoaderComponent from 'features/shared/Loader';
import { Text } from 'features/shared/ui/Page';
import { useClickStreamContext } from 'features/shared/ClickStream';

enum TabId {
  Students = 'students',
  Teachers = 'teachers',
  Classes = 'classes',
}

const tabs = [
  { id: TabId.Students, label: 'Количество учеников в параллелях' },
  { id: TabId.Teachers, label: 'Количество учителей' },
  { id: TabId.Classes, label: 'Количество классов' },
];

function parallelsSorter(a: Api.StatisticByParallel.Total, b: Api.StatisticByParallel.Total) {
  return Number(a.parallel) - Number(b.parallel);
}

function getPeopleCounts(samples: Api.StatisticByParallel.Total[], field: keyof Api.PeopleCounts) {
  return samples.sort(parallelsSorter).map((sample) => ({
    parallel: sample.parallel.toString(),
    value: sample[field],
  }));
}

function getClassesCounts(samples: Api.StatisticByParallel.Total[], start: number, end: number) {
  return samples
    .filter((sample) => Number(sample.parallel) >= start && Number(sample.parallel) <= end)
    .sort((s) => Number(s.parallel))
    .map((sample) => ({
      parallel: sample.parallel.toString(),
      value: sample.grade_count,
    }));
}

const Component = ({ filter, isRegion }: { filter: number | null; isRegion: boolean }) => {
  const { logPageEvent } = useClickStreamContext();

  const [tab, setTab] = useState(tabs[0]);
  const [statistic, setStatistic] = useState<Api.StatisticByParallel>();

  useEffect(() => {
    async function fetch() {
      try {
        const result = await SchoolsApi.getStatisticsByParallels();
        setStatistic(result);
      } catch (e) {}
    }

    fetch();
  }, []);

  const chartProps = useMemo(() => {
    if (!statistic) {
      return null;
    }
    const filteredStatistics =
      filter === null || filter === -1
        ? statistic.total
        : statistic.detailed_data.filter((el) => (isRegion ? el.subject_id : el.school_id) === filter);

    switch (tab.id) {
      case TabId.Students:
        return {
          series: [
            {
              title: 'зарегистрированы',
              data: getPeopleCounts(filteredStatistics, 'reg_student_count'),
              color: '#94C459',
            },
            {
              title: 'занимаются на платформе',
              data: getPeopleCounts(filteredStatistics, 'active_student_count'),
              color: '#76D19C',
            },
          ],
          valueAxisTitle: 'Количество учеников',
          isClustered: true,
          title:
            'Количество зарегистрированных и занимающихся на платформе учеников с начала учебного года и на момент просмотра графика.',
        };
      case TabId.Teachers:
        return {
          series: [
            {
              title: 'зарегистрировано',
              data: getPeopleCounts(filteredStatistics, 'reg_teacher_count'),
              color: '#E194E4',
            },
            {
              title: 'с активными учениками в классе',
              data: getPeopleCounts(filteredStatistics, 'active_teacher_count'),
              color: '#6872D4',
            },
          ],
          isClustered: true,
          valueAxisTitle: 'Количество учителей',
          title:
            'Количество зарегистрированных учителей и учителей с учениками в классе, которые занимаются на платформе, с начала учебного года и на момент просмотра графика.',
        };
      case TabId.Classes:
        return {
          series: [
            {
              title: 'начальная школа',
              data: getClassesCounts(filteredStatistics, 1, 4),
              color: '#2EC6C0',
            },
            {
              title: 'средняя школа',
              data: getClassesCounts(filteredStatistics, 5, 9),
              color: '#4D94FF',
            },
            {
              title: 'старшая школа',
              data: getClassesCounts(filteredStatistics, 10, 11),
              color: '#A779F8',
            },
          ],
          isClustered: false,
          valueAxisTitle: 'Количество классов',
          title:
            'Количество классов, которые занимаются на платформе с начала учебного года и на момент просмотра графика.',
        };
    }
  }, [tab.id, statistic, filter]);
  return !chartProps ? (
    <div style={{ width: '100%', height: '371px' }}>
      <LoaderComponent />
    </div>
  ) : (
    <>
      <SwitchTabs
        values={tabs}
        value={tab}
        handleChange={(value) => {
          setTab(value);
          logPageEvent({
            eventType: 'schools_statistic_by_parallels_select_chart_type_tab_' + value.id,
            eventInfo: 'Школы|Статистика по параллелям: Выбрал тип графика ' + value.label,
          });
        }}
        tabRenderer={BlueLinksTabSmall}
        qaPrefix="schools_statistic_"
      />
      <Spacer size={24} />
      <Text>{chartProps.title}</Text>
      <Spacer size={24} />
      <Chart yTitle={chartProps.valueAxisTitle} series={chartProps.series} isClustered={chartProps.isClustered} />
    </>
  );
};

export default Component;
